// material-ui
import { lighten, darken, getContrastRatio } from '@mui/material/styles';

// Rebuild a whole palette slot (primary, error, …) from a single brand colour so
// every tint/shade follows the brand, exactly as the built-in palettes do.
const ramp = (base, main) => ({
  ...base,
  lighter: lighten(main, 0.85),
  100: lighten(main, 0.8),
  200: lighten(main, 0.6),
  light: lighten(main, 0.4),
  400: lighten(main, 0.2),
  main,
  600: darken(main, 0.1),
  dark: darken(main, 0.2),
  800: darken(main, 0.35),
  darker: darken(main, 0.45),
  contrastText: getContrastRatio(main, '#ffffff') >= 3 ? '#ffffff' : '#1a1a1a'
});

/**
 * Layer a node's Branding over a base MUI palette. Shared by the live
 * ThemeCustomization (so the whole app repaints) and by the Advanced Settings
 * live preview (so what an admin sees is exactly what the pages render).
 *
 * The three "background" roles are a SURFACE system, mapped to what they visually
 * are across Ayro's pages:
 *   • Primary background   → primary.main   — hero banners, primary buttons, active nav
 *   • Secondary background → background.paper — the chrome/surfaces: top bar, sidebar,
 *                                               cards, panels, menus, dialogs
 *   • Tertiary background  → background.default — the page canvas behind everything
 *
 * Each "text" role is the foreground that sits ON its matching surface:
 *   • Primary text   → primary.contrastText — text on primary (e.g. a banner title)
 *   • Secondary text → text.primary         — the main reading text on cards/surfaces
 *   • Tertiary text  → text.secondary       — muted / secondary text
 *
 * Error / Success / Warning keep their own ramps. The MUI `secondary` palette is
 * deliberately left as Ayro's neutral grey (it is the app's neutral scale, not a
 * brand accent), so chrome like zebra-striped rows and hovers stays subtle.
 */
export const applyBranding = (palette, branding) => {
  const c = branding?.components;
  if (!c) return palette;
  return {
    ...palette,
    // Primary brand surface + the text that sits on it.
    primary: { ...ramp(palette.primary, c.primary_bg), contrastText: c.primary_text },
    error: ramp(palette.error, c.error),
    success: ramp(palette.success, c.success),
    warning: ramp(palette.warning, c.warning),
    // Surfaces: paper = cards/header/sidebar/menus; default = the page canvas.
    background: {
      ...palette.background,
      paper: c.secondary_bg,
      default: c.tertiary_bg
    },
    // Foreground on those surfaces: main reading text, then muted text.
    text: {
      ...palette.text,
      primary: c.secondary_text,
      secondary: c.tertiary_text
    }
  };
};

export default applyBranding;
